/**
 * Modal de configuração para node de pausar bot (Human Takeover)
 * Permite configurar tipo de pausa (indefinida ou temporizada)
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo dialog para sistema de pausa de bot
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { FlowNode, PauseBotConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

// Schema de validação
const pauseBotSchema = z.object({
  pause_type: z.enum(['indefinite', 'timed']),
  pause_duration: z.number().min(1).max(10080).optional(), // Max 7 dias (10080 min)
  pause_message: z.string().max(500).optional(),
});

interface ConfigurePauseBotDialogProps {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: PauseBotConfig) => void;
}

/**
 * Dialog de configuração de pausa de bot
 */
export function ConfigurePauseBotDialog({ tenantId, open, onOpenChange, node, onSave }: ConfigurePauseBotDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [pauseType, setPauseType] = useState<'indefinite' | 'timed'>('indefinite');
  const [pauseDuration, setPauseDuration] = useState<number>(30);
  const [pauseMessage, setPauseMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Inicializa valores do node
  useEffect(() => {
    if (node) {
      setPauseType(node.data.config?.pause_type || 'indefinite');
      setPauseDuration(node.data.config?.pause_duration || 30);
      setPauseMessage(node.data.config?.pause_message || '');
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      const config: PauseBotConfig = {
        pause_type: pauseType,
        ...(pauseType === 'timed' && { pause_duration: pauseDuration }),
        ...(pauseMessage && { pause_message: pauseMessage }),
        nickname: nicknameRef.current?.getNickname(),
      } as any;

      pauseBotSchema.parse(config);
      onSave(node.id, config);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Configurar Pausar Bot</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowHelp(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node (opcional, para referência) */}
          {node && <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node?.data.config?.nickname} />}

          {/* Tipo de Pausa */}
          <div className="space-y-2">
            <Label>Tipo de Pausa *</Label>
            <Select value={pauseType} onValueChange={(v: any) => setPauseType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="indefinite">
                  Indefinida (até ação manual)
                </SelectItem>
                <SelectItem value="timed">
                  Temporizada (por X minutos)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {pauseType === 'indefinite' 
                ? 'O bot ficará pausado até que um agente feche a conversa ou reative manualmente'
                : 'O bot será reativado automaticamente após o tempo especificado'
              }
            </p>
          </div>

          {/* Duração (apenas para pausa temporizada) */}
          {pauseType === 'timed' && (
            <div className="space-y-2">
              <Label>Duração (minutos) *</Label>
              <Input
                type="number"
                min={1}
                max={10080}
                value={pauseDuration}
                onChange={(e) => setPauseDuration(parseInt(e.target.value) || 30)}
              />
              <p className="text-xs text-muted-foreground">
                Máximo: 10080 minutos (7 dias). Sugestões: 30, 60, 120, 240, 480
              </p>
            </div>
          )}

          {/* Mensagem opcional */}
          <div className="space-y-2">
            <Label>Mensagem ao Pausar (opcional)</Label>
            <TextareaWithVariables
              tenantId={tenantId}
              value={pauseMessage}
              onChange={setPauseMessage}
              placeholder="Ex: Ok! Um atendente irá continuar a conversa em instantes..."
              maxLength={500}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Mensagem enviada ao contato quando o bot for pausado
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </DialogFooter>

        {/* Dialog de ajuda */}
        <NodeHelpDialog
          open={showHelp}
          onOpenChange={setShowHelp}
          nodeType="pause_bot"
        />
      </DialogContent>
    </Dialog>
  );
}
